import type { I18n } from "@lingui/core";
import { useLingui } from "@lingui/react";
import type { CarAd } from "@mobility-item-page/types";

/** The managed-value ids the vehicle lookup reports in `battery_type`. */
const BatteryTypeId = {
  LeadAcid: 4086,
  Lfp: 4085,
  LiIon: 4083,
  LiNmc: 4087,
  MoltenSalt: 1724,
  NiCd: 4088,
  NiMh: 4089,
  SolidState: 4084,
} as const;

type BatteryTypeCopy = { summary: string } | { whatItIs: string; meaning: string; comparison: string };

const batteryTypeCopy = (id: number | undefined, i18n: I18n): BatteryTypeCopy | undefined => {
  switch (id) {
    case BatteryTypeId.LeadAcid:
      return {
        summary: i18n.t({
          id: "itemPageLayout.ev.chargingCost.batteryType.leadAcid.summary",
          message:
            "Lead-acid is the familiar 12-volt battery that runs the lights, wipers, and screens. Almost every car has one, including electric cars, alongside the larger battery that drives the wheels. Ask the seller for details about this car’s drive battery.",
          comment: "Battery type row body on electric car ads, when the battery is Lead-Acid",
        }),
      };
    case BatteryTypeId.Lfp:
      return {
        whatItIs: i18n.t({
          id: "itemPageLayout.ev.chargingCost.batteryType.lfp.whatItIs",
          message:
            "LFP (Lithium Iron Phosphate) is used in most standard-range EVs and city cars. Built for durability rather than maximum range.",
          comment:
            "Battery type row body on electric car ads, when the battery is LFP (Lithium Iron Phosphate): what the battery is",
        }),
        meaning: i18n.t({
          id: "itemPageLayout.ev.chargingCost.batteryType.lfp.meaning",
          message:
            "You can charge it to 100% every day without extra wear, so there is no daily charge limit to manage. It handles thousands of charges, which means high mileage says less about this battery’s health than it would on other types.",
          comment:
            "Battery type row body on electric car ads, when the battery is LFP (Lithium Iron Phosphate): what it means for the buyer",
        }),
        comparison: i18n.t({
          id: "itemPageLayout.ev.chargingCost.batteryType.lfp.comparison",
          message:
            "For the same battery size in kWh, it gives slightly less range than the type used in long-range and premium EVs, and it charges more slowly below freezing. In return it lasts longer and costs less to replace.",
          comment:
            "Battery type row body on electric car ads, when the battery is LFP (Lithium Iron Phosphate): how it compares with other batteries",
        }),
      };
    case BatteryTypeId.LiIon:
      return {
        summary: i18n.t({
          id: "itemPageLayout.ev.chargingCost.batteryType.liIon.summary",
          message:
            "Lithium-ion is the family name for nearly every electric car battery, from small city cars to long-range models. It is usually shown when the specific type is not listed. For a used EV, the battery’s remaining health matters more than the exact type.",
          comment: "Battery type row body on electric car ads, when the battery is Li-Ion (generic Lithium-Ion)",
        }),
      };
    case BatteryTypeId.LiNmc:
      return {
        whatItIs: i18n.t({
          id: "itemPageLayout.ev.chargingCost.batteryType.liNmc.whatItIs",
          message:
            "Li-NMC (Lithium Nickel Manganese Cobalt) is used in most long-range EVs, premium models, and larger cars like SUVs. Built for range and charging speed.",
          comment:
            "Battery type row body on electric car ads, when the battery is Li-NMC (Lithium Nickel Manganese Cobalt): what the battery is",
        }),
        meaning: i18n.t({
          id: "itemPageLayout.ev.chargingCost.batteryType.liNmc.meaning",
          message:
            "More kilometers from the same battery size, and it copes better with cold winters and fast charging. Most manufacturers recommend charging to 80% day to day and saving 100% for long trips.",
          comment:
            "Battery type row body on electric car ads, when the battery is Li-NMC (Lithium Nickel Manganese Cobalt): what it means for the buyer",
        }),
        comparison: i18n.t({
          id: "itemPageLayout.ev.chargingCost.batteryType.liNmc.comparison",
          message:
            "For the same battery size in kWh, it gives more range than the type used in standard-range and city EVs, at a higher price.",
          comment:
            "Battery type row body on electric car ads, when the battery is Li-NMC (Lithium Nickel Manganese Cobalt): how it compares with other batteries",
        }),
      };
    case BatteryTypeId.MoltenSalt:
      return {
        whatItIs: i18n.t({
          id: "itemPageLayout.ev.chargingCost.batteryType.moltenSalt.whatItIs",
          message:
            "A specialist battery that runs hot inside, around 270-350°C, built from common materials like salt and nickel.",
          comment:
            "Battery type row body on electric car ads, when the battery is Molten Salt (Sodium-Nickel-Chloride / Zebra): what the battery is",
        }),
        meaning: i18n.t({
          id: "itemPageLayout.ev.chargingCost.batteryType.moltenSalt.meaning",
          message:
            "Freezing weather does not affect it while it is running, but it needs to stay warm, so it uses energy when the vehicle is parked for longer periods. Made for vehicles that drive continuously.",
          comment:
            "Battery type row body on electric car ads, when the battery is Molten Salt (Sodium-Nickel-Chloride / Zebra): what it means for the buyer",
        }),
        comparison: i18n.t({
          id: "itemPageLayout.ev.chargingCost.batteryType.moltenSalt.comparison",
          message:
            "Mainly found in commercial vehicles and fleet buses. Nearly every passenger EV uses a lithium battery instead, which holds its charge when parked.",
          comment:
            "Battery type row body on electric car ads, when the battery is Molten Salt (Sodium-Nickel-Chloride / Zebra): how it compares with other batteries",
        }),
      };
    case BatteryTypeId.NiCd:
      return {
        summary: i18n.t({
          id: "itemPageLayout.ev.chargingCost.batteryType.niCd.summary",
          message:
            "An older battery from 1990s electric vehicles and classic power tools. It is tough and handles vibration and rough use well, though today’s lithium batteries offer considerably more range for less weight. Rare in cars on the road today.",
          comment: "Battery type row body on electric car ads, when the battery is Ni-Cd (Nickel-Cadmium)",
        }),
      };
    case BatteryTypeId.NiMh:
      return {
        summary: i18n.t({
          id: "itemPageLayout.ev.chargingCost.batteryType.niMh.summary",
          message:
            "NiMH (Nickel-Metal Hydride) is the battery behind the first generations of self-charging hybrids, and still used today. It is safe and long-lasting, and built to support a petrol engine rather than drive the car on its own, so the pack is small and the electric-only range is limited.",
          comment: "Battery type row body on electric car ads, when the battery is NiMH (Nickel-Metal Hydride)",
        }),
      };
    case BatteryTypeId.SolidState:
      return {
        summary: i18n.t({
          id: "itemPageLayout.ev.chargingCost.batteryType.solidState.summary",
          message:
            "A newer battery design that replaces the liquid inside today’s cells with solid material. The technology is expected to bring longer range, faster charging, and lower fire risk, though it is still in development and very few cars use it so far.",
          comment: "Battery type row body on electric car ads, when the battery is Solid-State",
        }),
      };
    default:
      return undefined;
  }
};

type Props = {
  batteryType: CarAd["battery_type"];
};

/** Body of the battery type row: a short guide to the car's own battery, or a general note for one we have no guide to. */
export const BatteryTypeRowBody = ({ batteryType }: Props) => {
  const { i18n } = useLingui();
  const copy = batteryTypeCopy(batteryType?.id, i18n);

  if (!copy) {
    return i18n.t({
      id: "itemPageLayout.ev.chargingCost.batteryType.body",
      message:
        "The kind of battery the car has. They all do the same job — store the electricity the car runs on — but they differ a little in how far they take you and how they are best charged. The seller can tell you more about this one.",
      comment:
        "Body text of the battery type row on electric car ads, for a battery we have no guide to. Deliberately plain, for a reader who knows nothing about electric cars.",
    });
  }

  if ("summary" in copy) {
    return copy.summary;
  }

  const sections = [
    {
      title: i18n.t({
        id: "itemPageLayout.ev.chargingCost.batteryType.whatItIs.title",
        message: "What it is",
        comment: "Sub heading in the battery type row on electric car ads, above what the car's battery is",
      }),
      body: copy.whatItIs,
    },
    {
      title: i18n.t({
        id: "itemPageLayout.ev.chargingCost.batteryType.meaning.title",
        message: "What it means for you",
        comment:
          "Sub heading in the battery type row on electric car ads, above what the car's battery means for the buyer",
      }),
      body: copy.meaning,
    },
    {
      title: i18n.t({
        id: "itemPageLayout.ev.chargingCost.batteryType.comparison.title",
        message: "How it compares",
        comment:
          "Sub heading in the battery type row on electric car ads, above how the car's battery compares with other kinds",
      }),
      body: copy.comparison,
    },
  ];

  return (
    <div className="flex flex-col gap-16">
      {sections.map(({ title, body }) => (
        <div key={title}>
          <p className="font-bold">{title}</p>
          <p>{body}</p>
        </div>
      ))}
    </div>
  );
};
